import React, { useState } from 'react';
import {
  Modal,
  Button,
  Select,
  Form,
  Typography,
  message
} from 'antd';
import { 
  DownloadOutlined 
} from '@ant-design/icons';
import jsPDF from 'jspdf';
import apiService from "services/api/apiService";
import { useAuth } from '../../contexts/AuthContext';

const { Option } = Select;
const { Text } = Typography;

interface PDFExportModalProps {
  visible: boolean;
  onCancel: () => void;
  activeTab: string;
}

const PDFExportModal: React.FC<PDFExportModalProps> = ({ visible, onCancel, activeTab }) => {
  const [loading, setLoading] = useState(false);
  const [exportType, setExportType] = useState('job_postings');
  const { user } = useAuth();

  const generateJobPostingsPDF = async () => {
    try {
      setLoading(true);
      const response = await apiService.getJobPostings();
      
      if (response.success) {
        const jobs = response.data || [];
        const pdf = new jsPDF();
        
        // Add letterhead
        pdf.setFontSize(20);
        pdf.setTextColor(24, 144, 255); // Blue color
        pdf.text(user?.organization_name || 'TalentHub Nexus', 105, 30, { align: 'center' });
        
        pdf.setFontSize(12);
        pdf.setTextColor(100);
        pdf.text('Human Resources Department', 105, 40, { align: 'center' });
        pdf.text(`Date: ${new Date().toLocaleDateString()}`, 105, 50, { align: 'center' });
        
        // Add title
        pdf.setFontSize(16);
        pdf.setTextColor(0);
        pdf.text('Job Postings Report', 105, 70, { align: 'center' });
        
        let yPosition = 90;
        
        jobs.forEach((job: any, index: number) => {
          if (yPosition > 250) {
            pdf.addPage();
            yPosition = 30;
          }
          
          // Job title
          pdf.setFontSize(14);
          pdf.setTextColor(24, 144, 255);
          pdf.text(job.title, 20, yPosition);
          yPosition += 10;
          
          // Job details
          pdf.setFontSize(10);
          pdf.setTextColor(0);
          pdf.text(`Job ID: ${job.job_id}`, 20, yPosition);
          pdf.text(`Department: ${job.department}`, 120, yPosition);
          yPosition += 8;
          
          pdf.text(`Location: ${job.location}`, 20, yPosition);
          pdf.text(`Status: ${job.status.toUpperCase()}`, 120, yPosition);
          yPosition += 8;
          
          pdf.text(`Employment Type: ${job.employment_type}`, 20, yPosition);
          pdf.text(`Positions: ${job.positions_available}`, 120, yPosition);
          yPosition += 8;
          
          const salaryMin = parseFloat(job.salary_min);
          const salaryMax = parseFloat(job.salary_max);
          if (salaryMin > 0 || salaryMax > 0) {
            pdf.text(`Salary: NGN ${salaryMin.toLocaleString()} - NGN ${salaryMax.toLocaleString()} per annum`, 20, yPosition);
            yPosition += 8;
          }
          
          pdf.text(`Created: ${new Date(job.created_at).toLocaleDateString()}`, 20, yPosition);
          yPosition += 15;
          
          // Add separator line
          if (index < jobs.length - 1) {
            pdf.setDrawColor(200);
            pdf.line(20, yPosition, 190, yPosition);
            yPosition += 10;
          }
        });
        
        // Add footer
        const pageCount = pdf.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
          pdf.setPage(i);
          pdf.setFontSize(8);
          pdf.setTextColor(100);
          pdf.text(`Generated by Nexus HR System - Page ${i} of ${pageCount}`, 105, 285, { align: 'center' });
          pdf.text(`© ${new Date().getFullYear()} ${user?.organization_name || 'TalentHub Nexus'}. All rights reserved.`, 105, 290, { align: 'center' });
        }
        
        // Save PDF
        pdf.save(`job-postings-${new Date().toISOString().split('T')[0]}.pdf`);
        message.success('PDF exported successfully!');
        onCancel();
      }
    } catch (error) {
      message.error('Failed to generate PDF');
    } finally {
      setLoading(false);
    }
  };

  const generateJobOfferPDF = () => {
    const pdf = new jsPDF();
    
    // Add letterhead
    pdf.setFontSize(20);
    pdf.setTextColor(24, 144, 255);
    pdf.text(user?.organization_name || 'TalentHub Nexus', 105, 30, { align: 'center' });
    
    pdf.setFontSize(12);
    pdf.setTextColor(100);
    pdf.text('Human Resources Department', 105, 40, { align: 'center' });
    pdf.text(`Date: ${new Date().toLocaleDateString()}`, 105, 50, { align: 'center' });
    
    // Job offer content
    pdf.setFontSize(16);
    pdf.setTextColor(0);
    pdf.text('Job Offer Letter Template', 105, 70, { align: 'center' });
    
    pdf.setFontSize(12);
    let yPos = 90;
    
    const content = [
      '[Date]',
      '',
      '[Candidate Name]',
      '[Candidate Address]',
      '',
      'Dear [Candidate Name],',
      '',
      `We are pleased to offer you the position of [Job Title] with ${user?.organization_name || 'TalentHub Nexus'}.`,
      '',
      'Position Details:',
      '• Job Title: [Job Title]',
      '• Department: [Department]',
      '• Start Date: [Start Date]',
      '• Salary: NGN [Salary] per annum',
      '• Employment Type: [Employment Type]',
      '',
      'Please confirm your acceptance of this offer by [Date].',
      'We look forward to welcoming you to our team.',
      '',
      'Sincerely,',
      '',
      'HR Department',
      user?.organization_name || 'TalentHub Nexus'
    ];
    
    content.forEach(line => {
      if (yPos > 250) {
        pdf.addPage();
        yPos = 30;
      }
      pdf.text(line, 20, yPos);
      yPos += line === '' ? 5 : 8;
    });
    
    // Footer
    pdf.setFontSize(8);
    pdf.setTextColor(100);
    pdf.text('Generated by Nexus HR System', 105, 280, { align: 'center' });
    
    pdf.save(`job-offer-template-${new Date().toISOString().split('T')[0]}.pdf`);
    message.success('Job offer template exported successfully!');
    onCancel();
  };

  const handleExport = async () => {
    switch (exportType) {
      case 'job_postings':
        await generateJobPostingsPDF();
        break;
      case 'job_offer':
        generateJobOfferPDF();
        break;
      default:
        await generateJobPostingsPDF();
    }
  };

  return (
    <Modal
      title="Export Recruitment Data"
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button 
          key="export" 
          type="primary" 
          icon={<DownloadOutlined />}
          loading={loading}
          onClick={handleExport}
        >
          Export PDF
        </Button>
      ]}
    >
      <Form layout="vertical">
        <Form.Item label="Export Type">
          <Select value={exportType} onChange={setExportType}>
            <Option value="job_postings">Job Postings Report</Option>
            <Option value="job_offer">Job Offer Letter Template</Option>
          </Select>
        </Form.Item>
      </Form>

      <Text type="secondary">
        This will generate a PDF document with your organization's branded letterhead.
      </Text>
    </Modal>
  );
};

export default PDFExportModal;
